import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  Background,
  BackgroundVariant,
  BaseEdge,
  Controls,
  EdgeLabelRenderer,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  ReactFlow,
  getStraightPath,
  useEdgesState,
  useNodesState,
  useStore,
} from '@xyflow/react';
import type { Edge, EdgeProps, InternalNode, Node, NodeProps, ReactFlowInstance, XYPosition } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  ArrowRight,
  CircleDot,
  Download,
  GitBranch,
  Loader2,
  PlusCircle,
  RefreshCcw,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { useApiToken } from '@/auth/hooks';
import { useCanAccess } from '@/auth/useCanAccess';
import { ConceptDialog } from '@/app/features/editor/components/ConceptDialog';
import { DeleteConceptDialog } from '@/app/features/editor/components/DeleteConceptDialog';
import { PluginMetricCards } from '@/app/components/PluginMetricCards';
import { PluginPageHeader } from '@/app/components/PluginPageHeader';
import { ontologyManager } from '@/app/features/editor/services/ontologyManager';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import type {
  OntologyConcept,
  OntologyMetaType,
  OntologyRelationType,
  OntologySchema,
  OntologyStatus,
} from '@/types/ontology';

type ViewMode = 'graph' | 'tree';
type SidebarMode = 'concepts' | 'relations';
type SelectedItem =
  | { kind: 'concept'; id: string }
  | { kind: 'relation'; id: string }
  | null;

type SourceKind = 'core' | 'domain';

type ExplorerConcept = OntologyConcept & {
  sourceKind: SourceKind;
};

type ExplorerRelation = OntologyRelationType & {
  sourceKind: SourceKind;
};

type ConceptMetrics = {
  incoming: number;
  outgoing: number;
  undirected: number;
  relationIds: string[];
};

type GraphNodeData = {
  name: string;
  metaType: OntologyMetaType;
  status: OntologyStatus;
  sourceKind: SourceKind;
  fillColor: string;
  isSelected: boolean;
  isMuted: boolean;
};

type GraphFlowNode = Node<GraphNodeData>;
type StoredNodePositions = Record<string, XYPosition>;

type RelationSection = {
  key: 'directional' | 'undirected';
  label: string;
  items: ExplorerRelation[];
};

const CORE_DOMAIN_ID = '00000000-0000-0000-0000-000000000100';
const ONTOLOGY_GRAPH_LAYOUT_STORAGE_PREFIX = 'sopho-ontology-layout-v2';
const DOMAIN_NODE_SIZE = 64;
const CORE_NODE_SIZE = 60;
const NODE_WRAPPER_WIDTH = 120;
const NODE_SPACING_X = 110;
const NODE_SPACING_Y = 92;
const CIRCLE_RADIUS_X = 250;
const CIRCLE_RADIUS_Y = 190;
const CORE_HEXAGON_NORMALIZED_POINTS: Array<[number, number]> = [
  [0.25, 0.06],
  [0.75, 0.06],
  [1, 0.5],
  [0.75, 0.94],
  [0.25, 0.94],
  [0, 0.5],
];

const META_TYPE_ORDER: OntologyMetaType[] = [
  'Actor',
  'Spatial',
  'Asset',
  'Information',
  'Temporal',
  'DataPoint',
  'Quality',
  'Rule',
  'MasterData',
  'Process',
  'Situation',
  'Entity',
  'Source',
];

const META_TYPE_LABELS: Record<OntologyMetaType, string> = {
  Actor: 'Actor',
  Spatial: 'Spatial',
  Asset: 'Asset',
  Information: 'Information',
  Temporal: 'Temporal',
  DataPoint: 'DataPoint',
  Quality: 'Quality',
  Rule: 'Rule',
  MasterData: 'MasterData',
  Process: 'Process',
  Situation: 'Situation',
  Entity: 'Entity',
  Source: 'Source',
};

const META_TYPE_FILL: Record<OntologyMetaType, string> = {
  Actor: 'hsl(var(--ontology-node-actor))',
  Spatial: 'hsl(var(--ontology-node-spatial))',
  Asset: 'hsl(var(--ontology-node-asset))',
  Information: 'hsl(var(--ontology-node-information))',
  Temporal: 'hsl(var(--ontology-node-temporal))',
  DataPoint: 'hsl(var(--ontology-node-datapoint))',
  Quality: 'hsl(var(--ontology-node-quality))',
  Rule: 'hsl(var(--ontology-node-rule))',
  MasterData: 'hsl(var(--ontology-node-masterdata))',
  Process: 'hsl(var(--ontology-node-process))',
  Situation: 'hsl(var(--ontology-node-situation))',
  Entity: 'hsl(var(--ontology-node-entity))',
  Source: 'hsl(var(--ontology-node-source))',
};

const STATUS_ACCENTS = {
  CORE: 'border-slate-300/70 bg-slate-100 text-slate-700',
  APPROVED: 'border-emerald-300/70 bg-emerald-50 text-emerald-700',
} as const;

const SOURCE_ACCENTS: Record<SourceKind, string> = {
  core: 'border-slate-300/70 bg-slate-100 text-slate-700',
  domain: 'border-teal-300/70 bg-teal-50 text-teal-700',
};

const hiddenHandleStyle = {
  opacity: 0,
  width: 1,
  height: 1,
  border: 0,
  background: 'transparent',
} satisfies CSSProperties;

const handleDefinitions = [
  { suffix: 'top', position: Position.Top },
  { suffix: 'right', position: Position.Right },
  { suffix: 'bottom', position: Position.Bottom },
  { suffix: 'left', position: Position.Left },
] as const;

function normalizeSearch(value: string): string {
  return value.trim().toLowerCase();
}

function dedupeById<T extends { id: string }>(items: T[]): T[] {
  const map = new Map<string, T>();
  items.forEach((item) => map.set(item.id, item));
  return [...map.values()];
}

function sortConcepts(items: ExplorerConcept[]): ExplorerConcept[] {
  return [...items].sort((left, right) => {
    const typeDiff = META_TYPE_ORDER.indexOf(left.metaType) - META_TYPE_ORDER.indexOf(right.metaType);
    if (typeDiff !== 0) return typeDiff;
    const sourceDiff = left.sourceKind.localeCompare(right.sourceKind);
    if (sourceDiff !== 0) return sourceDiff;
    return left.name.localeCompare(right.name, 'de');
  });
}

function sortRelations(items: ExplorerRelation[]): ExplorerRelation[] {
  return [...items].sort((left, right) => left.name.localeCompare(right.name, 'de'));
}

function matchesConcept(concept: ExplorerConcept, query: string): boolean {
  if (!query) return true;
  const haystack = [concept.name, concept.description ?? '', concept.metaType, concept.status, concept.sourceKind]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

function matchesRelation(relation: ExplorerRelation, query: string): boolean {
  if (!query) return true;
  const haystack = [
    relation.name,
    relation.description ?? '',
    relation.sourceConceptName,
    relation.targetConceptName,
    relation.status,
    relation.sourceKind,
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

function buildOntologyLayout(concepts: ExplorerConcept[]): Map<string, XYPosition> {
  const positions = new Map<string, XYPosition>();
  const domainConcepts = concepts.filter((concept) => concept.sourceKind === 'domain');
  const coreConcepts = concepts.filter((concept) => concept.sourceKind === 'core');
  const groupedDomainConcepts = META_TYPE_ORDER.flatMap((metaType) => {
    const group = domainConcepts.filter((concept) => concept.metaType === metaType);
    return group.length > 0 ? [{ metaType, concepts: group }] : [];
  });

  const totalGroups = Math.max(groupedDomainConcepts.length, 1);
  groupedDomainConcepts.forEach((group, groupIndex) => {
    const baseAngle = (-Math.PI / 2) + ((Math.PI * 2) / totalGroups) * groupIndex;
    const groupSize = group.concepts.length;
    group.concepts.forEach((concept, conceptIndex) => {
      const offset = groupSize === 1 ? 0 : ((conceptIndex / (groupSize - 1)) - 0.5) * 0.52;
      const angle = baseAngle + offset;
      const radiusX = CIRCLE_RADIUS_X + (groupIndex % 2 === 0 ? 16 : -12);
      const radiusY = CIRCLE_RADIUS_Y + (conceptIndex % 2 === 0 ? 12 : -8);
      positions.set(concept.id, {
        x: 430 + Math.cos(angle) * radiusX,
        y: 250 + Math.sin(angle) * radiusY,
      });
    });
  });

  const columns = Math.min(4, Math.max(coreConcepts.length, 1));
  const startX = 420 - ((columns - 1) * NODE_SPACING_X) / 2;
  const startY = 300;
  coreConcepts.forEach((concept, index) => {
    const row = Math.floor(index / columns);
    const column = index % columns;
    positions.set(concept.id, {
      x: startX + column * NODE_SPACING_X + (row % 2 === 0 ? 0 : NODE_SPACING_X / 2),
      y: startY + row * NODE_SPACING_Y,
    });
  });

  return positions;
}

function getNodeSize(sourceKind: SourceKind): number {
  return sourceKind === 'core' ? CORE_NODE_SIZE : DOMAIN_NODE_SIZE;
}

function getNodeFrameSize(sourceKind: SourceKind): number {
  return getNodeSize(sourceKind) + 24;
}

function getNodeShapeCenter(node: InternalNode<GraphFlowNode>): XYPosition {
  const sourceKind = node.data?.sourceKind ?? 'domain';
  const frameSize = getNodeFrameSize(sourceKind);
  const { x, y } = node.internals.positionAbsolute;

  return {
    x: x + NODE_WRAPPER_WIDTH / 2,
    y: y + frameSize / 2,
  };
}

function getNodeShapeTopLeft(node: InternalNode<GraphFlowNode>): XYPosition {
  const sourceKind = node.data?.sourceKind ?? 'domain';
  const nodeSize = getNodeSize(sourceKind);
  const frameSize = getNodeFrameSize(sourceKind);
  const { x, y } = node.internals.positionAbsolute;

  return {
    x: x + (NODE_WRAPPER_WIDTH - nodeSize) / 2,
    y: y + (frameSize - nodeSize) / 2,
  };
}

function crossProduct(left: XYPosition, right: XYPosition): number {
  return left.x * right.y - left.y * right.x;
}

function getCircleBoundaryPoint(center: XYPosition, target: XYPosition, radius: number): XYPosition {
  const deltaX = target.x - center.x;
  const deltaY = target.y - center.y;
  const distance = Math.hypot(deltaX, deltaY);

  if (distance === 0) {
    return { x: center.x, y: center.y - radius };
  }

  return {
    x: center.x + (deltaX / distance) * radius,
    y: center.y + (deltaY / distance) * radius,
  };
}

function getHexagonBoundaryPoint(node: InternalNode<GraphFlowNode>, target: XYPosition): XYPosition {
  const center = getNodeShapeCenter(node);
  const topLeft = getNodeShapeTopLeft(node);
  const nodeSize = getNodeSize('core');
  const points = CORE_HEXAGON_NORMALIZED_POINTS.map(([x, y]) => ({
    x: topLeft.x + x * nodeSize,
    y: topLeft.y + y * nodeSize,
  }));
  const direction = { x: target.x - center.x, y: target.y - center.y };

  if (direction.x === 0 && direction.y === 0) {
    return { x: center.x, y: topLeft.y };
  }

  let closestIntersection: (XYPosition & { distance: number }) | null = null;

  points.forEach((point, index) => {
    const nextPoint = points[(index + 1) % points.length];
    const segment = { x: nextPoint.x - point.x, y: nextPoint.y - point.y };
    const denominator = crossProduct(direction, segment);

    if (Math.abs(denominator) < 0.0001) {
      return;
    }

    const relativePoint = { x: point.x - center.x, y: point.y - center.y };
    const rayDistance = crossProduct(relativePoint, segment) / denominator;
    const segmentDistance = crossProduct(relativePoint, direction) / denominator;

    if (rayDistance < 0 || segmentDistance < 0 || segmentDistance > 1) {
      return;
    }

    const intersection = {
      x: center.x + direction.x * rayDistance,
      y: center.y + direction.y * rayDistance,
      distance: rayDistance,
    };

    if (!closestIntersection || intersection.distance < closestIntersection.distance) {
      closestIntersection = intersection;
    }
  });

  return closestIntersection ?? center;
}

function getNodeBoundaryPoint(node: InternalNode<GraphFlowNode>, target: XYPosition): XYPosition {
  const sourceKind = node.data?.sourceKind ?? 'domain';
  const center = getNodeShapeCenter(node);

  if (sourceKind === 'core') {
    return getHexagonBoundaryPoint(node, target);
  }

  return getCircleBoundaryPoint(center, target, getNodeSize(sourceKind) / 2);
}

function getSelfLoopPath(node: InternalNode<GraphFlowNode>): { path: string; labelX: number; labelY: number } {
  const center = getNodeShapeCenter(node);
  const nodeSize = getNodeSize(node.data?.sourceKind ?? 'domain');
  const start = getNodeBoundaryPoint(node, {
    x: center.x + nodeSize * 1.8,
    y: center.y - nodeSize * 0.92,
  });
  const end = getNodeBoundaryPoint(node, {
    x: center.x + nodeSize * 1.8,
    y: center.y + nodeSize * 0.92,
  });
  const mid = { x: center.x + nodeSize * 1.66, y: center.y };
  const control1 = { x: center.x + nodeSize * 0.96, y: center.y - nodeSize * 1.08 };
  const control2 = { x: center.x + nodeSize * 1.66, y: center.y - nodeSize * 0.94 };
  const control3 = { x: center.x + nodeSize * 1.66, y: center.y + nodeSize * 0.94 };
  const control4 = { x: center.x + nodeSize * 0.96, y: center.y + nodeSize * 1.08 };

  return {
    path: `M ${start.x} ${start.y} C ${control1.x} ${control1.y}, ${control2.x} ${control2.y}, ${mid.x} ${mid.y} C ${control3.x} ${control3.y}, ${control4.x} ${control4.y}, ${end.x} ${end.y}`,
    labelX: center.x + nodeSize * 1.22,
    labelY: center.y - nodeSize * 0.98,
  };
}

function OntologyRelationEdge({
  id,
  source,
  target,
  label,
  labelStyle,
  labelBgStyle,
  style,
  markerEnd,
}: EdgeProps<Edge>) {
  const selector = useCallback((state: { nodeLookup: unknown }) => {
    const nodeLookup = state.nodeLookup as Map<string, InternalNode<GraphFlowNode>>;

    return {
      sourceNode: nodeLookup.get(source),
      targetNode: nodeLookup.get(target),
    };
  }, [source, target]);

  const { sourceNode, targetNode } = useStore(selector);

  if (!sourceNode || !targetNode) {
    return null;
  }

  let edgePath = '';
  let labelX = 0;
  let labelY = 0;

  if (source === target) {
    const selfLoop = getSelfLoopPath(sourceNode);
    edgePath = selfLoop.path;
    labelX = selfLoop.labelX;
    labelY = selfLoop.labelY;
  } else {
    const sourceCenter = getNodeShapeCenter(sourceNode);
    const targetCenter = getNodeShapeCenter(targetNode);
    const sourceBoundary = getNodeBoundaryPoint(sourceNode, targetCenter);
    const targetBoundary = getNodeBoundaryPoint(targetNode, sourceCenter);
    [edgePath, labelX, labelY] = getStraightPath({
      sourceX: sourceBoundary.x,
      sourceY: sourceBoundary.y,
      targetX: targetBoundary.x,
      targetY: targetBoundary.y,
    });
  }

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={style} />
      {typeof label === 'string' && label.length > 0 && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'none',
              color: labelStyle?.fill,
              fontSize: labelStyle?.fontSize,
              fontWeight: labelStyle?.fontWeight,
              backgroundColor: labelBgStyle?.fill,
              borderRadius: labelBgStyle?.borderRadius,
            }}
            className="px-2 py-0.5"
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

function getOntologyGraphStorageKey(domainId: string): string {
  return `${ONTOLOGY_GRAPH_LAYOUT_STORAGE_PREFIX}:${domainId}`;
}

function readStoredNodePositions(domainId: string | null): StoredNodePositions {
  if (!domainId || typeof window === 'undefined') {
    return {};
  }

  try {
    const rawValue = window.localStorage.getItem(getOntologyGraphStorageKey(domainId));
    if (!rawValue) {
      return {};
    }

    const parsed = JSON.parse(rawValue) as Record<string, Partial<XYPosition>>;
    return Object.fromEntries(
      Object.entries(parsed).flatMap(([id, position]) => {
        if (typeof position?.x !== 'number' || typeof position?.y !== 'number') {
          return [];
        }

        if (!Number.isFinite(position.x) || !Number.isFinite(position.y)) {
          return [];
        }

        if (Math.abs(position.x) > 4000 || Math.abs(position.y) > 4000) {
          return [];
        }

        return [[id, { x: position.x, y: position.y }]];
      }),
    );
  } catch {
    return {};
  }
}

function writeStoredNodePositions(domainId: string, positions: StoredNodePositions): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(getOntologyGraphStorageKey(domainId), JSON.stringify(positions));
}

function mergeStoredNodePositions(
  concepts: ExplorerConcept[],
  autoPositions: Map<string, XYPosition>,
  storedPositions: StoredNodePositions,
): Map<string, XYPosition> {
  const mergedPositions = new Map(autoPositions);

  concepts.forEach((concept) => {
    const storedPosition = storedPositions[concept.id];
    if (storedPosition) {
      mergedPositions.set(concept.id, storedPosition);
    }
  });

  return mergedPositions;
}

function OntologyConceptNode({ data }: NodeProps<GraphFlowNode>) {
  const isCore = data.sourceKind === 'core';
  const nodeSize = isCore ? CORE_NODE_SIZE : DOMAIN_NODE_SIZE;
  const haloSize = nodeSize + 16;
  const frameSize = nodeSize + 24;
  const coreHexagonClip = 'polygon(25% 6%, 75% 6%, 100% 50%, 75% 94%, 25% 94%, 0% 50%)';
  const coreHexagonPoints = '25,6 75,6 100,50 75,94 25,94 0,50';
  const selectedBorderColor = isCore
    ? 'hsl(var(--ontology-graph-core-selected-border))'
    : 'hsl(var(--ontology-graph-primary))';
  const shapeStyle: CSSProperties = isCore
    ? {
        width: nodeSize,
        height: nodeSize,
        clipPath: coreHexagonClip,
      }
    : {
        width: nodeSize,
        height: nodeSize,
        borderRadius: 999,
        backgroundColor: data.fillColor,
        borderColor: data.isSelected ? selectedBorderColor : 'hsl(var(--ontology-graph-border))',
      };

  return (
    <div className="relative flex w-[120px] flex-col items-center gap-2" style={{ opacity: data.isMuted ? 0.24 : 1 }}>
      <div
        className="relative flex items-center justify-center"
        style={{ width: frameSize, height: frameSize }}
      >
        {data.isSelected && isCore && (
          <svg
            className="pointer-events-none absolute"
            width={haloSize}
            height={haloSize}
            viewBox="0 0 100 100"
            aria-hidden="true"
            style={{
              filter: 'drop-shadow(0 12px 24px hsl(var(--ontology-graph-shadow) / 0.18))',
            }}
          >
            <polygon
              points={coreHexagonPoints}
              fill="none"
              stroke="hsl(var(--ontology-graph-core-fill))"
              strokeWidth="1.2"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        )}
        {data.isSelected && !isCore && (
          <div
            className="pointer-events-none absolute"
            style={{
              width: haloSize,
              height: haloSize,
              borderRadius: 999,
              border: '2px solid hsl(var(--ontology-graph-primary) / 0.42)',
              backgroundColor: 'hsl(var(--ontology-graph-primary-soft) / 0.46)',
              boxShadow: '0 12px 24px hsl(var(--ontology-graph-shadow) / 0.16)',
            }}
            aria-hidden="true"
          />
        )}
        <div
          className={`relative z-10 flex items-center justify-center shadow-sm transition-all ${isCore ? '' : 'border-2'}`}
          style={{
            ...shapeStyle,
            boxShadow: data.isSelected
              ? isCore
                ? '0 10px 18px hsl(var(--ontology-graph-shadow) / 0.12)'
                : '0 0 0 3px hsl(var(--ontology-graph-primary) / 0.14)'
              : '0 10px 18px hsl(var(--ontology-graph-shadow) / 0.12)',
            borderColor: data.isSelected ? selectedBorderColor : shapeStyle.borderColor,
          }}
        >
        {isCore && (
          <svg
            className="pointer-events-none absolute inset-0"
            viewBox="0 0 100 100"
            aria-hidden="true"
          >
            <polygon
              points={coreHexagonPoints}
              fill="hsl(var(--ontology-graph-core-fill))"
              stroke="none"
            />
            <polygon
              points={coreHexagonPoints}
              fill="none"
              stroke={data.isSelected ? 'hsl(var(--ontology-graph-core-selected-border))' : 'hsl(var(--ontology-graph-core-border))'}
              strokeWidth="1.2"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        )}
        {handleDefinitions.map((handle) => (
          <Handle
            key={`target-${handle.suffix}`}
            id={`target-${handle.suffix}`}
            type="target"
            position={handle.position}
            style={hiddenHandleStyle}
          />
        ))}
        {handleDefinitions.map((handle) => (
          <Handle
            key={`source-${handle.suffix}`}
            id={`source-${handle.suffix}`}
            type="source"
            position={handle.position}
            style={hiddenHandleStyle}
          />
        ))}
        </div>
      </div>
      <div
        className="max-w-[120px] text-center text-xs font-medium leading-4"
        style={{ color: 'hsl(var(--ontology-graph-foreground))' }}
      >
        {data.name}
      </div>
    </div>
  );
}

const nodeTypes = {
  ontologyConcept: OntologyConceptNode,
};

const edgeTypes = {
  ontologyRelation: OntologyRelationEdge,
};

export default function SophoOntologyPage() {
  const { activeWorkspace } = useWorkspaceStore();
  const { getAccessToken } = useApiToken();
  const { checkFeature } = useCanAccess();
  const canEdit = checkFeature('sopho_ontology', 'edit-ontology');
  const canDelete = checkFeature('sopho_ontology', 'delete-ontology');

  const [concepts, setConcepts] = useState<ExplorerConcept[]>([]);
  const [relations, setRelations] = useState<ExplorerRelation[]>([]);
  const [schema, setSchema] = useState<OntologySchema | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [token, setToken] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('graph');
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('concepts');
  const [search, setSearch] = useState('');
  const [metaTypeFilter, setMetaTypeFilter] = useState<string>('all');
  const [selectedItem, setSelectedItem] = useState<SelectedItem>(null);
  const [editingConcept, setEditingConcept] = useState<ExplorerConcept | null>(null);
  const [isConceptDialogOpen, setIsConceptDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance<GraphFlowNode, Edge> | null>(null);
  const [hoveredNode, setHoveredNode] = useState<{ id: string; x: number; y: number } | null>(null);
  const [hasAutoFittedGraph, setHasAutoFittedGraph] = useState(false);
  const graphContainerRef = useRef<HTMLDivElement | null>(null);

  const deferredSearch = useDeferredValue(search);
  const domainId = activeWorkspace?.domain?.id ?? null;
  const domainLabel = activeWorkspace?.domain?.label ?? 'Keine Domain aktiv';
  const isCoreWorkspace = domainId === CORE_DOMAIN_ID;

  useEffect(() => {
    setHasAutoFittedGraph(false);
  }, [domainId]);

  const storedNodePositions = useMemo(() => readStoredNodePositions(domainId), [domainId]);

  useEffect(() => {
    if (!domainId) {
      setConcepts([]);
      setRelations([]);
      setSchema(null);
      setSelectedItem(null);
      setError(null);
      return;
    }

    let cancelled = false;

    async function loadOntology() {
      setLoading(true);
      setError(null);

      try {
        if (!domainId) return;
        const activeDomainId: string = domainId;
        const nextToken = await getAccessToken().catch(() => null);
        if (cancelled) return;
        setToken(nextToken);

        const domainConceptsPromise = ontologyManager.fetchConcepts(activeDomainId, nextToken);
        const domainRelationsPromise = ontologyManager.fetchRelations(activeDomainId, nextToken);
        const schemaPromise = ontologyManager.fetchSchema(activeDomainId, nextToken);

        const coreConceptsPromise = isCoreWorkspace
          ? Promise.resolve<OntologyConcept[]>([])
          : ontologyManager.fetchConcepts(CORE_DOMAIN_ID, nextToken).catch(() => []);
        const coreRelationsPromise = isCoreWorkspace
          ? Promise.resolve<OntologyRelationType[]>([])
          : ontologyManager.fetchRelations(CORE_DOMAIN_ID, nextToken).catch(() => []);

        const [domainConcepts, domainRelations, fetchedSchema, coreConcepts, coreRelations] = await Promise.all([
          domainConceptsPromise,
          domainRelationsPromise,
          schemaPromise,
          coreConceptsPromise,
          coreRelationsPromise,
        ]);

        if (cancelled) return;

        const mergedConcepts = dedupeById([
          ...coreConcepts.map((concept) => ({ ...concept, sourceKind: 'core' as const })),
          ...domainConcepts.map((concept) => ({ ...concept, sourceKind: concept.domainId === CORE_DOMAIN_ID ? 'core' as const : 'domain' as const })),
        ]);
        const mergedRelations = dedupeById([
          ...coreRelations.map((relation) => ({ ...relation, sourceKind: 'core' as const })),
          ...domainRelations.map((relation) => ({ ...relation, sourceKind: relation.domainId === CORE_DOMAIN_ID ? 'core' as const : 'domain' as const })),
        ]);

        setConcepts(sortConcepts(mergedConcepts));
        setRelations(sortRelations(mergedRelations));
        setSchema(fetchedSchema as OntologySchema);
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : 'Ontologie konnte nicht geladen werden.');
          setConcepts([]);
          setRelations([]);
          setSchema(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadOntology();

    return () => {
      cancelled = true;
    };
  }, [domainId, getAccessToken, isCoreWorkspace, reloadTick]);

  const conceptById = useMemo(() => new Map(concepts.map((concept) => [concept.id, concept])), [concepts]);
  const relationById = useMemo(() => new Map(relations.map((relation) => [relation.id, relation])), [relations]);

  const conceptMetrics = useMemo(() => {
    const metrics = new Map<string, ConceptMetrics>();
    concepts.forEach((concept) => {
      metrics.set(concept.id, { incoming: 0, outgoing: 0, undirected: 0, relationIds: [] });
    });

    relations.forEach((relation) => {
      const sourceMetrics = metrics.get(relation.sourceConceptId);
      const targetMetrics = metrics.get(relation.targetConceptId);

      if (sourceMetrics) {
        sourceMetrics.relationIds.push(relation.id);
        if (relation.isDirectional) sourceMetrics.outgoing += 1;
        else sourceMetrics.undirected += 1;
      }

      if (targetMetrics) {
        targetMetrics.relationIds.push(relation.id);
        if (relation.isDirectional) targetMetrics.incoming += 1;
        else targetMetrics.undirected += 1;
      }
    });

    return metrics;
  }, [concepts, relations]);

  const visibleData = useMemo(() => {
    const normalizedQuery = normalizeSearch(deferredSearch);
    const metaFilteredConcepts = metaTypeFilter === 'all'
      ? concepts
      : concepts.filter((concept) => concept.metaType === metaTypeFilter);
    const allowedConceptIds = new Set(metaFilteredConcepts.map((concept) => concept.id));
    const conceptMatches = metaFilteredConcepts.filter((concept) => matchesConcept(concept, normalizedQuery));
    const conceptMatchIds = new Set(conceptMatches.map((concept) => concept.id));

    const relationMatches = relations.filter((relation) => {
      const sourceAllowed = allowedConceptIds.has(relation.sourceConceptId);
      const targetAllowed = allowedConceptIds.has(relation.targetConceptId);
      if (!sourceAllowed && !targetAllowed) return false;
      return matchesRelation(relation, normalizedQuery);
    });

    const visibleConceptIds = new Set<string>();
    if (normalizedQuery) {
      conceptMatches.forEach((concept) => visibleConceptIds.add(concept.id));
      relationMatches.forEach((relation) => {
        if (allowedConceptIds.has(relation.sourceConceptId)) visibleConceptIds.add(relation.sourceConceptId);
        if (allowedConceptIds.has(relation.targetConceptId)) visibleConceptIds.add(relation.targetConceptId);
      });
    } else {
      metaFilteredConcepts.forEach((concept) => visibleConceptIds.add(concept.id));
    }

    const visibleConcepts = metaFilteredConcepts.filter((concept) => visibleConceptIds.has(concept.id));
    const visibleRelations = relations.filter((relation) => {
      const endpointsVisible = visibleConceptIds.has(relation.sourceConceptId) && visibleConceptIds.has(relation.targetConceptId);
      if (!endpointsVisible) return false;
      if (!normalizedQuery) return true;
      return relationMatches.some((match) => match.id === relation.id)
        || conceptMatchIds.has(relation.sourceConceptId)
        || conceptMatchIds.has(relation.targetConceptId);
    });

    const conceptSections = META_TYPE_ORDER.map((metaType) => ({
      metaType,
      label: META_TYPE_LABELS[metaType],
      concepts: visibleConcepts.filter((concept) => concept.metaType === metaType),
    })).filter((section) => section.concepts.length > 0);

    return {
      visibleConcepts,
      visibleRelations,
      conceptSections,
      directionalRelations: visibleRelations.filter((relation) => relation.isDirectional),
      undirectedRelations: visibleRelations.filter((relation) => !relation.isDirectional),
      coreConceptCount: visibleConcepts.filter((concept) => concept.sourceKind === 'core').length,
      domainConceptCount: visibleConcepts.filter((concept) => concept.sourceKind === 'domain').length,
    };
  }, [concepts, deferredSearch, metaTypeFilter, relations]);

  const relationSections = useMemo<RelationSection[]>(() => {
    const sections: RelationSection[] = [
      { key: 'directional', label: 'Relationen', items: visibleData.directionalRelations },
      { key: 'undirected', label: 'Bidirektional', items: visibleData.undirectedRelations },
    ];

    return sections.filter((section) => section.items.length > 0);
  }, [visibleData.directionalRelations, visibleData.undirectedRelations]);

  useEffect(() => {
    if (!selectedItem) return;

    if (selectedItem.kind === 'concept') {
      const conceptIsVisible = visibleData.visibleConcepts.some((concept) => concept.id === selectedItem.id);
      if (!conceptIsVisible) {
        setSelectedItem(null);
      }
      return;
    }

    const relationIsVisible = visibleData.visibleRelations.some((relation) => relation.id === selectedItem.id);
    if (!relationIsVisible) {
      setSelectedItem(null);
    }
  }, [selectedItem, visibleData.visibleConcepts, visibleData.visibleRelations]);

  const selectedConcept = selectedItem?.kind === 'concept' ? conceptById.get(selectedItem.id) ?? null : null;
  const selectedRelation = selectedItem?.kind === 'relation' ? relationById.get(selectedItem.id) ?? null : null;
  const hoveredConcept = hoveredNode ? conceptById.get(hoveredNode.id) ?? null : null;

  const graphHighlight = useMemo(() => {
    const visibleNodeIds = new Set<string>();
    const activeEdgeIds = new Set<string>();
    const selectedNodeId = selectedConcept?.id ?? null;

    if (selectedConcept) {
      visibleNodeIds.add(selectedConcept.id);
      visibleData.visibleRelations.forEach((relation) => {
        if (relation.sourceConceptId === selectedConcept.id || relation.targetConceptId === selectedConcept.id) {
          activeEdgeIds.add(relation.id);
          visibleNodeIds.add(relation.sourceConceptId);
          visibleNodeIds.add(relation.targetConceptId);
        }
      });
    }

    if (selectedRelation) {
      activeEdgeIds.add(selectedRelation.id);
      visibleNodeIds.add(selectedRelation.sourceConceptId);
      visibleNodeIds.add(selectedRelation.targetConceptId);
    }

    return {
      visibleNodeIds,
      activeEdgeIds,
      selectedNodeId,
      hasSelection: selectedConcept !== null || selectedRelation !== null,
    };
  }, [selectedConcept, selectedRelation, visibleData.visibleRelations]);

  const graphLayout = useMemo(() => {
    const positions = mergeStoredNodePositions(
      visibleData.visibleConcepts,
      buildOntologyLayout(visibleData.visibleConcepts),
      storedNodePositions,
    );

    const nodes: GraphFlowNode[] = visibleData.visibleConcepts.map((concept) => ({
      id: concept.id,
      type: 'ontologyConcept',
      position: positions.get(concept.id) ?? { x: 0, y: 0 },
      draggable: true,
      data: {
        name: concept.name,
        metaType: concept.metaType,
        status: concept.status,
        sourceKind: concept.sourceKind,
        fillColor: META_TYPE_FILL[concept.metaType],
        isSelected: graphHighlight.selectedNodeId === concept.id,
        isMuted: graphHighlight.hasSelection && !graphHighlight.visibleNodeIds.has(concept.id),
      },
    }));

    const edges: Edge[] = visibleData.visibleRelations.map((relation) => {
      const isActive = graphHighlight.activeEdgeIds.has(relation.id);

      return {
        id: relation.id,
        source: relation.sourceConceptId,
        target: relation.targetConceptId,
        type: 'ontologyRelation',
        animated: false,
        label: isActive ? relation.name : undefined,
        style: {
          stroke: isActive ? 'hsl(var(--ontology-graph-primary))' : 'hsl(var(--ontology-graph-edge))',
          strokeWidth: isActive ? 2.6 : 1.2,
          strokeLinecap: 'round',
          opacity: graphHighlight.hasSelection && !isActive ? 0.24 : 0.95,
        },
        labelStyle: {
          fill: 'hsl(var(--ontology-graph-primary-strong))',
          fontSize: 11,
          fontWeight: 700,
        },
        labelBgStyle: {
          fill: 'hsl(var(--ontology-graph-card) / 0.96)',
          borderRadius: 999,
        },
        markerEnd: relation.isDirectional
          ? {
              type: MarkerType.ArrowClosed,
              width: 18,
              height: 18,
              color: isActive ? 'hsl(var(--ontology-graph-primary))' : 'hsl(var(--ontology-graph-edge))',
            }
          : undefined,
      };
    });

    return { nodes, edges };
  }, [graphHighlight.activeEdgeIds, graphHighlight.hasSelection, graphHighlight.selectedNodeId, graphHighlight.visibleNodeIds, storedNodePositions, visibleData.visibleConcepts, visibleData.visibleRelations]);

  const [nodes, setNodes, onNodesChange] = useNodesState<GraphFlowNode>(graphLayout.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(graphLayout.edges);

  const handleNodeDragStop = useCallback((_: React.MouseEvent, node: GraphFlowNode) => {
    if (!domainId) {
      return;
    }

    const nextPositions = {
      ...readStoredNodePositions(domainId),
      [node.id]: node.position,
    } satisfies StoredNodePositions;

    writeStoredNodePositions(domainId, nextPositions);
  }, [domainId]);

  useEffect(() => {
    setNodes((currentNodes) => {
      const currentNodeMap = new Map(currentNodes.map((node) => [node.id, node]));

      return graphLayout.nodes.map((nextNode) => {
        const currentNode = currentNodeMap.get(nextNode.id);
        if (!currentNode) {
          return nextNode;
        }

        return {
          ...nextNode,
          position: currentNode.position,
        };
      });
    });
    setEdges(graphLayout.edges);
  }, [graphLayout.edges, graphLayout.nodes, setEdges, setNodes]);

  useEffect(() => {
    if (!reactFlowInstance || nodes.length === 0 || hasAutoFittedGraph) return;
    const frame = requestAnimationFrame(() => {
      void reactFlowInstance.fitView({ padding: 0.16, duration: 280 });
      setHasAutoFittedGraph(true);
    });
    return () => cancelAnimationFrame(frame);
  }, [graphLayout.nodes, hasAutoFittedGraph, nodes.length, reactFlowInstance]);

  const updateHoveredNode = (clientX: number, clientY: number, nodeId: string) => {
    const containerRect = graphContainerRef.current?.getBoundingClientRect();
    if (!containerRect) return;

    setHoveredNode({
      id: nodeId,
      x: clientX - containerRect.left,
      y: clientY - containerRect.top,
    });
  };

  const selectedConceptRelations = useMemo(() => {
    if (!selectedConcept) {
      return { incoming: [] as ExplorerRelation[], outgoing: [] as ExplorerRelation[], undirected: [] as ExplorerRelation[] };
    }

    return {
      incoming: relations.filter((relation) => relation.isDirectional && relation.targetConceptId === selectedConcept.id),
      outgoing: relations.filter((relation) => relation.isDirectional && relation.sourceConceptId === selectedConcept.id),
      undirected: relations.filter(
        (relation) => !relation.isDirectional && (relation.sourceConceptId === selectedConcept.id || relation.targetConceptId === selectedConcept.id),
      ),
    };
  }, [relations, selectedConcept]);

  const availableMetaTypes = useMemo(
    () => META_TYPE_ORDER.filter((metaType) => concepts.some((concept) => concept.metaType === metaType)),
    [concepts],
  );

  const schemaNodeMatches = useMemo(() => {
    if (!schema || !selectedConcept) return [];
    return schema.allowedNodes.filter((node) => node.metaType === selectedConcept.metaType).slice(0, 4);
  }, [schema, selectedConcept]);

  const relationEndpointSummary = selectedRelation
    ? {
        source: conceptById.get(selectedRelation.sourceConceptId) ?? null,
        target: conceptById.get(selectedRelation.targetConceptId) ?? null,
      }
    : null;

  const handleReload = () => setReloadTick((current) => current + 1);

  const handleExport = () => {
    const payload = {
      workspace: activeWorkspace,
      generatedAt: new Date().toISOString(),
      concepts: visibleData.visibleConcepts,
      relations: visibleData.visibleRelations,
      schema,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sopho-ontology-${activeWorkspace?.slugId ?? 'workspace'}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const conceptList = visibleData.conceptSections.length > 0 ? (
    <div className="space-y-4">
      {visibleData.conceptSections.map((section) => (
        <div key={section.metaType} className="rounded-2xl border border-border/70 bg-card/70 p-3">
          <div className="mb-2 flex items-center justify-between gap-3">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{section.label}</div>
              <div className="text-[11px] text-muted-foreground">{section.concepts.length} Konzepte</div>
            </div>
            <Badge variant="outline" className="border-slate-300/70 bg-slate-50 text-slate-700">
              {section.metaType}
            </Badge>
          </div>
          <div className="space-y-1.5">
            {section.concepts.map((concept) => {
              const isSelected = selectedItem?.kind === 'concept' && selectedItem.id === concept.id;
              const metrics = conceptMetrics.get(concept.id);
              return (
                <button
                  key={concept.id}
                  type="button"
                  onClick={() => setSelectedItem({ kind: 'concept', id: concept.id })}
                  className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left transition ${
                    isSelected
                      ? 'border-teal-300 bg-teal-50 shadow-sm'
                      : 'border-transparent bg-background/40 hover:border-border hover:bg-background/70'
                  }`}
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">{concept.name}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                      <Badge variant="outline" className={SOURCE_ACCENTS[concept.sourceKind]}>
                        {concept.sourceKind === 'core' ? 'Core' : 'Domain'}
                      </Badge>
                      <span>{metrics ? metrics.relationIds.length : 0} Rel.</span>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  ) : (
    <div className="rounded-2xl border border-dashed border-border bg-card/50 p-6 text-sm text-muted-foreground">
      Keine Konzepte für den aktuellen Filter.
    </div>
  );

  const relationList = visibleData.visibleRelations.length > 0 ? (
    <div className="space-y-3">
      {relationSections.map((section) => (
          <div key={section.key} className="rounded-2xl border border-border/70 bg-card/70 p-3">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{section.label}</div>
            <div className="space-y-1.5">
              {section.items.map((relation) => {
                const isSelected = selectedItem?.kind === 'relation' && selectedItem.id === relation.id;
                return (
                  <button
                    key={relation.id}
                    type="button"
                    onClick={() => setSelectedItem({ kind: 'relation', id: relation.id })}
                    className={`flex w-full items-start justify-between rounded-xl border px-3 py-2 text-left transition ${
                      isSelected
                        ? 'border-teal-300 bg-teal-50 shadow-sm'
                        : 'border-transparent bg-background/40 hover:border-border hover:bg-background/70'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-foreground">{relation.name}</div>
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        {relation.sourceConceptName} {relation.isDirectional ? '->' : '<->'} {relation.targetConceptName}
                      </div>
                    </div>
                    <Badge variant="outline" className={SOURCE_ACCENTS[relation.sourceKind]}>
                      {relation.sourceKind === 'core' ? 'Core' : 'Domain'}
                    </Badge>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
    </div>
  ) : (
    <div className="rounded-2xl border border-dashed border-border bg-card/50 p-6 text-sm text-muted-foreground">
      Keine Relationen für den aktuellen Filter.
    </div>
  );

  return (
    <div className="flex h-full w-full min-w-0 flex-col gap-6 overflow-hidden px-4 py-6 sm:px-6 xl:px-8 2xl:px-10">
      <PluginPageHeader
        group="analysis"
        title="Sopho Ontology"
        icon={GitBranch}
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" onClick={handleExport} disabled={!domainId || loading}>
              <Download className="mr-2 h-4 w-4" /> Export
            </Button>
            <Button type="button" variant="outline" onClick={handleReload} disabled={!domainId || loading}>
              <RefreshCcw className="mr-2 h-4 w-4" /> Reload
            </Button>
            {canEdit ? (
              <Button
                type="button"
                onClick={() => {
                  setEditingConcept(null);
                  setIsConceptDialogOpen(true);
                }}
                disabled={!domainId}
              >
                <PlusCircle className="mr-2 h-4 w-4" /> Konzept
              </Button>
            ) : null}
          </div>
        )}
      />

      <PluginMetricCards
        className="gap-3 md:grid-cols-2 xl:grid-cols-4"
        items={[
          { id: 'domain', label: 'Domain', value: domainLabel, detail: activeWorkspace?.domain?.coreOntologyName ?? 'Kein Core-Mapping', icon: GitBranch, tone: 'neutral' },
          { id: 'concepts', label: 'Konzepte', value: visibleData.visibleConcepts.length, icon: CircleDot, tone: 'info' },
          { id: 'core', label: 'Core', value: visibleData.coreConceptCount, icon: ShieldCheck, tone: 'success' },
          { id: 'domain-count', label: 'Domain', value: visibleData.domainConceptCount, icon: GitBranch, tone: 'warning' },
        ]}
      />

      <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[320px_minmax(0,1fr)_320px]">
        <Card className="flex min-h-0 flex-col overflow-hidden border-border/70 bg-card/70">
          <CardHeader className="space-y-4 border-b border-border/70 pb-4">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">Explorer</CardTitle>
              <div className="inline-flex rounded-xl border border-border/70 bg-background/50 p-1">
                <button
                  type="button"
                  onClick={() => setSidebarMode('concepts')}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${sidebarMode === 'concepts' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  Konzepte ({visibleData.visibleConcepts.length})
                </button>
                <button
                  type="button"
                  onClick={() => setSidebarMode('relations')}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${sidebarMode === 'relations' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  Relationen ({visibleData.visibleRelations.length})
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="pl-9"
                  placeholder="Konzepte suchen..."
                />
              </div>
              <Select value={metaTypeFilter} onValueChange={setMetaTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Meta Type filtern" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Meta Types</SelectItem>
                  {availableMetaTypes.map((metaType) => (
                    <SelectItem key={metaType} value={metaType}>
                      {META_TYPE_LABELS[metaType]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="min-h-0 flex-1 overflow-y-auto p-4">
            {sidebarMode === 'concepts' ? conceptList : relationList}
          </CardContent>
        </Card>

        <Card className="flex min-h-0 flex-col overflow-hidden border-border/70 bg-card/70">
          <CardHeader className="border-b border-border/70 pb-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">Graph Canvas</CardTitle>
                <div className="mt-1 text-sm text-muted-foreground">
                  Zoom, Pan und Fit All sind direkt im Canvas verfuegbar. Listen-Selektion highlightet zugehoerige Knoten und Beziehungen.
                </div>
              </div>
              <div className="inline-flex rounded-xl border border-border/70 bg-background/50 p-1">
                <button
                  type="button"
                  onClick={() => setViewMode('tree')}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${viewMode === 'tree' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  Baum
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('graph')}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${viewMode === 'graph' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  Graph
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="min-h-0 flex-1 p-0">
            {loading ? (
              <div className="flex h-[560px] w-full items-center justify-center text-muted-foreground xl:h-full">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Ontologie wird geladen...
              </div>
            ) : error ? (
              <div className="flex h-[560px] w-full flex-col items-center justify-center gap-3 px-6 text-center xl:h-full">
                <div className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {error}
                </div>
                <Button type="button" variant="outline" onClick={handleReload}>Erneut versuchen</Button>
              </div>
            ) : !domainId ? (
              <div className="flex h-[560px] w-full items-center justify-center px-6 text-center text-sm text-muted-foreground xl:h-full">
                Aktiviere zuerst einen Workspace mit Domain-Zuordnung, damit die Ontologie geladen werden kann.
              </div>
            ) : viewMode === 'graph' ? (
              <div
                ref={graphContainerRef}
                className="ontology-graph-surface relative h-[560px] w-full xl:h-full"
                style={{
                  background: 'linear-gradient(180deg, hsl(var(--ontology-graph-background)), hsl(var(--ontology-graph-muted) / 0.82))',
                }}
              >
                <ReactFlow<GraphFlowNode, Edge>
                  className="ontology-graph-flow"
                  nodes={nodes}
                  edges={edges}
                  nodeTypes={nodeTypes}
                  edgeTypes={edgeTypes}
                  onInit={(instance) => setReactFlowInstance(instance)}
                  onNodesChange={onNodesChange}
                  onNodeDragStop={handleNodeDragStop}
                  onEdgesChange={onEdgesChange}
                  onNodeClick={(_, node) => setSelectedItem({ kind: 'concept', id: node.id })}
                  onEdgeClick={(_, edge) => setSelectedItem({ kind: 'relation', id: edge.id })}
                  onNodeMouseEnter={(event, node) => updateHoveredNode(event.clientX, event.clientY, node.id)}
                  onNodeMouseMove={(event, node) => updateHoveredNode(event.clientX, event.clientY, node.id)}
                  onNodeMouseLeave={() => setHoveredNode(null)}
                  onPaneClick={() => {
                    setSelectedItem(null);
                    setHoveredNode(null);
                  }}
                  fitView
                  fitViewOptions={{ padding: 0.16 }}
                  minZoom={0.35}
                  maxZoom={2.2}
                  nodesDraggable
                  nodesConnectable={false}
                  elementsSelectable
                  proOptions={{ hideAttribution: true }}
                >
                  <Background
                    variant={BackgroundVariant.Dots}
                    gap={22}
                    size={1}
                    color="hsl(var(--ontology-graph-border) / 0.72)"
                  />
                  <MiniMap
                    pannable
                    zoomable
                    className="ontology-graph-minimap"
                    maskColor="hsl(var(--ontology-graph-background) / 0.72)"
                    nodeColor={(node) => {
                      const data = node.data as GraphNodeData | undefined;
                      if (!data) return '#cbd5e1';
                      return data.sourceKind === 'core' ? 'hsl(var(--ontology-graph-muted))' : data.fillColor;
                    }}
                  />
                  <Controls className="ontology-graph-controls" />
                </ReactFlow>
                {hoveredConcept && hoveredNode && (
                  <div
                    className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-full rounded-xl border px-3 py-1.5 text-xs font-medium shadow-sm backdrop-blur-sm"
                    style={{
                      borderColor: 'hsl(var(--ontology-graph-border))',
                      backgroundColor: 'hsl(var(--ontology-graph-card) / 0.96)',
                      color: 'hsl(var(--ontology-graph-foreground))',
                      left: hoveredNode.x,
                      top: hoveredNode.y - 14,
                    }}
                  >
                    {META_TYPE_LABELS[hoveredConcept.metaType]} · {conceptMetrics.get(hoveredConcept.id)?.relationIds.length ?? 0} Rel.
                  </div>
                )}
              </div>
            ) : (
              <div className="h-[560px] overflow-y-auto p-4 xl:h-full">
                {visibleData.conceptSections.length > 0 ? (
                  <div className="space-y-4">
                    {visibleData.conceptSections.map((section) => (
                      <details key={section.metaType} open className="rounded-2xl border border-border/70 bg-background/40 p-4">
                        <summary className="cursor-pointer list-none text-sm font-semibold text-foreground">
                          <div className="flex items-center justify-between gap-3">
                            <span>{section.label}</span>
                            <Badge variant="outline" className="border-slate-300/70 bg-slate-50 text-slate-700">
                              {section.concepts.length}
                            </Badge>
                          </div>
                        </summary>
                        <div className="mt-3 space-y-2 border-l border-border pl-4">
                          {section.concepts.map((concept) => {
                            const isSelected = selectedItem?.kind === 'concept' && selectedItem.id === concept.id;
                            const metrics = conceptMetrics.get(concept.id);
                            return (
                              <button
                                key={concept.id}
                                type="button"
                                onClick={() => setSelectedItem({ kind: 'concept', id: concept.id })}
                                className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left transition ${
                                  isSelected
                                    ? 'border-teal-300 bg-teal-50 shadow-sm'
                                    : 'border-border/50 bg-card/80 hover:border-border hover:bg-card'
                                }`}
                              >
                                <div>
                                  <div className="text-sm font-medium text-foreground">{concept.name}</div>
                                  <div className="mt-1 text-[11px] text-muted-foreground">
                                    {metrics ? `${metrics.relationIds.length} verknuepfte Relationen` : 'Keine Relationen'}
                                  </div>
                                </div>
                                <Badge variant="outline" className={SOURCE_ACCENTS[concept.sourceKind]}>
                                  {concept.sourceKind === 'core' ? 'Core' : 'Domain'}
                                </Badge>
                              </button>
                            );
                          })}
                        </div>
                      </details>
                    ))}
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    Die Baumansicht hat für den aktuellen Filter keine Einträge.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="flex min-h-0 flex-col overflow-hidden border-border/70 bg-card/70">
          <CardHeader className="border-b border-border/70 pb-4">
            <CardTitle className="text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">Details</CardTitle>
          </CardHeader>
          <CardContent className="min-h-0 flex-1 overflow-y-auto p-4">
            {selectedConcept ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="text-lg font-semibold text-foreground">{selectedConcept.name}</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className={SOURCE_ACCENTS[selectedConcept.sourceKind]}>
                      {selectedConcept.sourceKind === 'core' ? 'Core' : 'Domain'}
                    </Badge>
                    <Badge variant="outline" className={STATUS_ACCENTS[selectedConcept.status]}>
                      {selectedConcept.status}
                    </Badge>
                    <Badge variant="outline" className="border-slate-300/70 bg-slate-50 text-slate-700">
                      {selectedConcept.metaType}
                    </Badge>
                  </div>
                </div>

                <p className="text-sm leading-6 text-muted-foreground">
                  {selectedConcept.description || 'Zu diesem Konzept liegt aktuell keine Beschreibung vor.'}
                </p>

                {canEdit && selectedConcept.sourceKind === 'domain' && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setEditingConcept(selectedConcept);
                        setIsConceptDialogOpen(true);
                      }}
                    >
                      Konzept bearbeiten
                    </Button>
                    {canDelete && selectedConcept.status !== 'CORE' && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setEditingConcept(selectedConcept);
                          setIsDeleteDialogOpen(true);
                        }}
                      >
                        Konzept loeschen
                      </Button>
                    )}
                  </div>
                )}

                <div className="grid gap-3 grid-cols-3">
                  <Card className="border-border/70 bg-background/40">
                    <CardContent className="p-3">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Ausgehend</div>
                      <div className="mt-1 text-xl font-semibold text-foreground">{selectedConceptRelations.outgoing.length}</div>
                    </CardContent>
                  </Card>
                  <Card className="border-border/70 bg-background/40">
                    <CardContent className="p-3">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Eingehend</div>
                      <div className="mt-1 text-xl font-semibold text-foreground">{selectedConceptRelations.incoming.length}</div>
                    </CardContent>
                  </Card>
                  <Card className="border-border/70 bg-background/40">
                    <CardContent className="p-3">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Verbunden</div>
                      <div className="mt-1 text-xl font-semibold text-foreground">{selectedConceptRelations.undirected.length}</div>
                    </CardContent>
                  </Card>
                </div>

                <div className="space-y-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Beziehungen</div>
                  <div className="space-y-2">
                    {[...selectedConceptRelations.outgoing, ...selectedConceptRelations.incoming, ...selectedConceptRelations.undirected].length > 0 ? (
                      [...selectedConceptRelations.outgoing, ...selectedConceptRelations.incoming, ...selectedConceptRelations.undirected].map((relation) => (
                        <button
                          key={relation.id}
                          type="button"
                          onClick={() => setSelectedItem({ kind: 'relation', id: relation.id })}
                          className="flex w-full items-center justify-between rounded-xl border border-border/70 bg-background/40 px-3 py-2 text-left hover:bg-background/70"
                        >
                          <div>
                            <div className="text-sm font-medium text-foreground">{relation.name}</div>
                            <div className="mt-1 text-[11px] text-muted-foreground">
                              {relation.sourceConceptName} {relation.isDirectional ? '->' : '<->'} {relation.targetConceptName}
                            </div>
                          </div>
                          <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        </button>
                      ))
                    ) : (
                      <div className="rounded-2xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                        Für dieses Konzept gibt es aktuell keine verknüpften Relationen.
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Schema-Hinweise</div>
                  {schemaNodeMatches.length > 0 ? (
                    <div className="space-y-2">
                      {schemaNodeMatches.map((node) => (
                        <div key={`${node.metaType}-${node.name}`} className="rounded-xl border border-border/70 bg-background/40 px-3 py-2">
                          <div className="text-sm font-medium text-foreground">{node.name}</div>
                          <div className="mt-1 text-xs text-muted-foreground">{node.description}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                      Keine Schema-Hinweise für den aktuell selektierten Meta Type gefunden.
                    </div>
                  )}
                </div>
              </div>
            ) : selectedRelation && relationEndpointSummary ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="text-lg font-semibold text-foreground">{selectedRelation.name}</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className={SOURCE_ACCENTS[selectedRelation.sourceKind]}>
                      {selectedRelation.sourceKind === 'core' ? 'Core' : 'Domain'}
                    </Badge>
                    <Badge variant="outline" className={STATUS_ACCENTS[selectedRelation.status]}>
                      {selectedRelation.status}
                    </Badge>
                  </div>
                </div>
                <p className="text-sm leading-6 text-muted-foreground">
                  {selectedRelation.description || 'Zu dieser Relation liegt aktuell keine Beschreibung vor.'}
                </p>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => relationEndpointSummary.source && setSelectedItem({ kind: 'concept', id: relationEndpointSummary.source.id })}
                    className="flex w-full items-center justify-between rounded-xl border border-border/70 bg-background/40 px-3 py-3 text-left hover:bg-background/70"
                  >
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Source</div>
                      <div className="mt-1 text-sm font-medium text-foreground">{relationEndpointSummary.source?.name ?? selectedRelation.sourceConceptName}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                  <button
                    type="button"
                    onClick={() => relationEndpointSummary.target && setSelectedItem({ kind: 'concept', id: relationEndpointSummary.target.id })}
                    className="flex w-full items-center justify-between rounded-xl border border-border/70 bg-background/40 px-3 py-3 text-left hover:bg-background/70"
                  >
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Target</div>
                      <div className="mt-1 text-sm font-medium text-foreground">{relationEndpointSummary.target?.name ?? selectedRelation.targetConceptName}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex h-full min-h-[420px] flex-col items-center justify-center gap-3 text-center text-sm text-muted-foreground">
                <div className="rounded-2xl border border-dashed border-border px-5 py-4">
                  Konzept oder Relation links auswaehlen, um die Canvas-Selektion und Details zu sehen.
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <ConceptDialog
        open={isConceptDialogOpen}
        onOpenChange={setIsConceptDialogOpen}
        domainId={domainId ?? ''}
        concept={editingConcept}
        token={token}
        onSuccess={() => {
          setIsConceptDialogOpen(false);
          setEditingConcept(null);
          handleReload();
        }}
      />

      <DeleteConceptDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        concept={editingConcept}
        token={token}
        onSuccess={() => {
          setIsDeleteDialogOpen(false);
          setEditingConcept(null);
          handleReload();
        }}
      />
    </div>
  );
}
